import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { StyleSheet, useColorScheme, type ImageStyle, type TextStyle, type ViewStyle } from 'react-native';
import { KEYS, storage } from '../lib/storage';

const light = {
  bg: '#f5f6f8',
  surface: '#ffffff',
  surface2: '#f9fafb',
  hover: '#f1f3f6',
  border: '#e4e7ec',
  borderStrong: '#cfd4dc',
  text: '#111827',
  muted: '#5b6474',
  faint: '#8a93a3',
  primary: '#4f46e5',
  primaryHover: '#4338ca',
  primarySoft: '#eef0ff',
  onPrimary: '#ffffff',
  success: '#067647',
  successSoft: '#e7f6ee',
  warning: '#a15c07',
  warningSoft: '#fdf3e1',
  danger: '#c01f2f',
  dangerSoft: '#fdecee',
  info: '#1d63c9',
  infoSoft: '#e8f1fd',
  neutral: '#475467',
  neutralSoft: '#eef0f3',
  overlay: 'rgba(16, 24, 40, 0.45)',
  sidebar: '#ffffff',
};
export type Colors = typeof light;

const dark: Colors = {
  bg: '#0d1017',
  surface: '#151a23',
  surface2: '#1a202b',
  hover: '#202734',
  border: '#283040',
  borderStrong: '#37414f',
  text: '#e8ebf1',
  muted: '#a0a9b8',
  faint: '#6f7888',
  primary: '#8b87ff',
  primaryHover: '#a19eff',
  primarySoft: '#252443',
  onPrimary: '#0d1017',
  success: '#4fd08b',
  successSoft: '#16301f',
  warning: '#f2b54c',
  warningSoft: '#33280f',
  danger: '#ff7b86',
  dangerSoft: '#3a1a1e',
  info: '#72aaff',
  infoSoft: '#172a45',
  neutral: '#a0a9b8',
  neutralSoft: '#232a36',
  overlay: 'rgba(0, 0, 0, 0.6)',
  sidebar: '#11151d',
};

export const spacing = { xxs: 2, xs: 4, sm: 8, md: 12, lg: 16, xl: 24, xxl: 32 } as const;
export const radius = { sm: 6, md: 8, lg: 12, xl: 16, pill: 999 } as const;
export const font = {
  size: { xs: 11, sm: 12, md: 13, base: 14, lg: 16, xl: 20, xxl: 26, display: 34 },
  weight: { regular: '400', medium: '500', semibold: '600', bold: '700' },
} as const;

export type Tone = 'success' | 'warning' | 'danger' | 'info' | 'neutral' | 'primary';
export const toneColors = (c: Colors, tone: Tone): { fg: string; bg: string } => {
  const map: Record<Tone, [string, string]> = {
    success: [c.success, c.successSoft],
    warning: [c.warning, c.warningSoft],
    danger: [c.danger, c.dangerSoft],
    info: [c.info, c.infoSoft],
    neutral: [c.neutral, c.neutralSoft],
    primary: [c.primary, c.primarySoft],
  };
  const [fg, bg] = map[tone];
  return { fg, bg };
};

export type ThemeMode = 'system' | 'light' | 'dark';
interface ThemeValue {
  colors: Colors;
  dark: boolean;
  mode: ThemeMode;
  setMode: (m: ThemeMode) => void;
  toggle: () => void;
}
const ThemeCtx = createContext<ThemeValue | null>(null);

/** Light/dark theme: follows the OS until the user toggles; choice persisted in AsyncStorage. */
export function ThemeProvider({ children }: { children: React.ReactNode }) {
  const scheme = useColorScheme();
  const [mode, setModeState] = useState<ThemeMode>(() => (storage.peek(KEYS.themeMode) as ThemeMode) || 'system');
  const isDark = mode === 'system' ? scheme === 'dark' : mode === 'dark';
  const setMode = useCallback((m: ThemeMode) => {
    setModeState(m);
    void storage.set(KEYS.themeMode, m === 'system' ? null : m);
  }, []);
  const toggle = useCallback(() => setMode(isDark ? 'light' : 'dark'), [isDark, setMode]);
  const value = useMemo(() => ({ colors: isDark ? dark : light, dark: isDark, mode, setMode, toggle }), [isDark, mode, setMode, toggle]);
  useEffect(() => {
    if (typeof document !== 'undefined') {
      document.body.style.background = value.colors.bg;
      document.documentElement.style.colorScheme = isDark ? 'dark' : 'light';
    }
  }, [value.colors.bg, isDark]);
  return <ThemeCtx.Provider value={value}>{children}</ThemeCtx.Provider>;
}

export function useTheme() {
  const t = useContext(ThemeCtx);
  if (!t) throw new Error('ThemeProvider missing');
  return t;
}

/**
 * Themed StyleSheet factory:
 *   const useStyles = makeStyles((c) => ({ box: { backgroundColor: c.surface } }));
 *   const s = useStyles();
 */
type NamedStyles<T> = { [P in keyof T]: ViewStyle | TextStyle | ImageStyle };
export function makeStyles<T extends NamedStyles<T>>(fn: (c: Colors) => T & NamedStyles<T>) {
  const cache = new WeakMap<Colors, T>();
  return function useStyles(): T {
    const { colors } = useTheme();
    let s = cache.get(colors);
    if (!s) {
      s = StyleSheet.create(fn(colors)) as T;
      cache.set(colors, s);
    }
    return s;
  };
}
